import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Divider, Empty, Form, notification, Row, Skeleton, Typography, Spin } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useScopes } from 'hooks/useScopes';
import { SalesforceAccountSearch } from 'components/atoms/SalesforceAccountSearch';
import { useClientDetail, useDeleteClientRelation, useUpdateClient, useUpdateClientLogoFilename } from 'sb/api/client';
import { ClientForm } from 'components/atoms/ClientForm';
import { ClientRelatedCompanies } from 'components/atoms/ClientRelatedCompanies';
import { useQueryClient } from '@tanstack/react-query';
import { ClientManagementSummary } from 'sb/models/Client';
import { FileUploadResult } from 'sb/models/Files';
import { CompanyRelationshipModal } from 'components/molecules/admin/CompanyRelationshipModal';
import { ApiCard } from 'components/api-card/ApiCard';
import {
  useClientApiLimits,
  useSaveClientEndpointExceptions,
  useSaveClientEndpointOverride
} from 'api/site_administration';
import { HttpMethod, Exception, Endpoint, ApiGroup } from 'types/SiteAdministration';
import { durationToUnit, unitToDuration } from './utils/formatters';

interface SalesforceAccount {
  value: string;
  label: string;
  key: string;
}

const Clients: React.FC = () => {
  const [form] = Form.useForm();
  const { aclCheck } = useScopes();

  // LOCAL STATE
  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const [isParentCompanyEditEnabled, setIsParentCompanyEditEnabled] = useState(false);
  const [isAddClicked, setIsAddClicked] = useState(false);
  const [salesforceAcc, setSalesforceAcc] = useState<SalesforceAccount>();

  const [apiGroups, setApiGroups] = useState<ApiGroup[]>([]);
  const [userOptions, setUserOptions] = useState<{ label: string; value: string }[]>([]);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [savingGroupId, setSavingGroupId] = useState<string | null>(null);

  const deleteRalatedCompany = (id: number) => {
    deleteClientRelation(id);
  };

  const resetEditAndAddStates = () => {
    setIsEditEnabled(false);
    setIsAddClicked(false);
  };

  const setFormValues = useCallback(
    (_client: ClientManagementSummary) => {
      form.setFieldsValue({
        encrypted_name: _client?.encrypted_name,
        salesforce_id: _client?.salesforce_id || salesforceAcc?.value,
        industry: _client?.industry,
        sub_industry: _client?.sub_industry,
        annual_revenue_millions: _client?.annual_revenue_millions,
        encrypted_domain: _client?.encrypted_domain,
        auth0_organization_id: _client?.auth0_organization_id,
        logo_url: _client?.logo_url
      });
      form.setFieldValue(['salesforce_account', 'Name'], _client?.salesforce_account?.Name);
    },
    [form, salesforceAcc?.value]
  );

  // Queries and Mutations
  const queryClient = useQueryClient();
  const {
    data: client,
    isLoading,
    isInitialLoading
  } = useClientDetail(salesforceAcc?.value || '', {
    enabled: !!salesforceAcc?.value,
    onSuccess: setFormValues,
    onError: form.resetFields
  });

  const { data: clientApiLimits, isLoading: isLoadingApiLimits } = useClientApiLimits(client?.id);
  const { mutateAsync: saveClientEndpointOverride } = useSaveClientEndpointOverride(client?.id);
  const { mutateAsync: saveClientEndpointExceptions } = useSaveClientEndpointExceptions(client?.id);

  useEffect(() => {
    if (client) {
      setFormValues(client);
    }
  }, [client, setFormValues]);

  useEffect(() => {
    if (clientApiLimits && clientApiLimits.integrations) {
      // Map user options
      const users = clientApiLimits.users.map((u) => ({
        label: `${u.firstName} ${u.lastName}`,
        value: u.id
      }));
      setUserOptions(users);

      // Map endpoints
      const groups: ApiGroup[] = clientApiLimits.integrations.map((integration) => ({
        id: integration.moduleName,
        title: integration.moduleName,
        showExceptions: true,
        endpoints: integration.endpoints.map((ep) => ({
          endpointId: ep.endpointId,
          method: ep.httpMethod as HttpMethod,
          path: ep.pathTemplate,
          limit: ep.effectiveClient?.points ?? ep.effectiveGlobal.points,
          unit: durationToUnit(ep.effectiveClient?.durationSeconds ?? ep.effectiveGlobal.durationSeconds),
          exceptions: (ep.userExceptionGroups || []).map((ueg) => ({
            limit: ueg.limit.points,
            unit: durationToUnit(ueg.limit.durationSeconds),
            users: ueg.userIds
          }))
        }))
      }));
      setApiGroups(groups);
    }
  }, [clientApiLimits]);

  const { mutate: updateClient, isLoading: isClientUpdating } = useUpdateClient({
    onSuccess: () => {
      queryClient.invalidateQueries(['clientDetail', salesforceAcc?.value]).then();
      notification.success({
        message: 'Client updated successfully',
        duration: 4
      });
    },
    onSettled: resetEditAndAddStates,
    onError: () => {
      notification.error({
        message: 'Something went wrong while updating client. Please try again.',
        duration: 4
      });
    }
  });

  const { mutate: deleteClientRelation } = useDeleteClientRelation({
    onSuccess: () => {
      queryClient.invalidateQueries(['clientDetail', salesforceAcc?.value]).then();
      notification.success({
        message: 'Company relation deleted successfully',
        duration: 4
      });
    },
    onError: () => {
      notification.error({
        message: 'Something went wrong while deleting client relation. Please try again.',
        duration: 4
      });
    }
  });

  // Logo upload
  const { mutate: uploadLogo } = useUpdateClientLogoFilename({
    onSuccess: () => {
      queryClient.invalidateQueries(['clientDetail', salesforceAcc?.value]).then();
    }
  });

  useEffect(() => {
    // Reset states when client changes
    resetEditAndAddStates();
  }, [salesforceAcc?.value]);

  const handleClientChange = async (_salesforceAcc: SalesforceAccount) => {
    form.resetFields();
    setSalesforceAcc(_salesforceAcc);
  };

  const handleSubmit = async (_values: any) => {
    updateClient({ ..._values, salesforceId: client?.salesforce_id });
  };

  const handleAdd = () => {
    setIsAddClicked(true);
  };

  const handleEdit = () => {
    setIsEditEnabled(true);
  };

  const handleEditParentCompany = () => {
    setIsParentCompanyEditEnabled(true);
  };

  const mapEditedExceptions = (exceptions: Exception[] = []) =>
    exceptions.map((ex) => ({
      limit: {
        points: ex.limit ?? 0,
        durationSeconds: unitToDuration(ex.unit)
      },
      userIds: [...ex.users].sort()
    }));

  const mapOriginalExceptions = (originalEp?: any) =>
    (originalEp?.userExceptionGroups || []).map((ueg: any) => ({
      limit: {
        points: ueg.limit.points,
        durationSeconds: ueg.limit.durationSeconds
      },
      userIds: [...ueg.userIds].sort()
    }));

  const sortExceptionGroups = (groups: { limit: { points: number; durationSeconds: number }; userIds: string[] }[]) =>
    [...groups].sort((a, b) => {
      const aKey = `${a.limit.points}-${a.limit.durationSeconds}-${a.userIds.join(',')}`;
      const bKey = `${b.limit.points}-${b.limit.durationSeconds}-${b.userIds.join(',')}`;
      return aKey.localeCompare(bKey);
    });

  const handleSaveApiLimits = async (groupId: string, endpoints: Endpoint[]) => {
    if (!clientApiLimits) return;

    try {
      const originalIntegration = clientApiLimits.integrations.find((i) => i.moduleName === groupId);

      let currentRevisionToken = clientApiLimits.clientRevisionToken;
      let hasChanges = false;

      for (const ep of endpoints) {
        const originalEp = originalIntegration?.endpoints.find((oep) => oep.endpointId === ep.endpointId);

        if (originalEp) {
          const originalLimit = originalEp.effectiveClient?.points ?? originalEp.effectiveGlobal.points;
          const originalUnit = durationToUnit(
            originalEp.effectiveClient?.durationSeconds ?? originalEp.effectiveGlobal.durationSeconds
          );

          const overrideChanged = ep.limit !== originalLimit || ep.unit !== originalUnit;
          if (overrideChanged) {
            hasChanges = true;
            setSavingGroupId(groupId);
            const overrideResult = await saveClientEndpointOverride({
              endpointId: ep.endpointId,
              data: {
                clientRevisionToken: currentRevisionToken,
                desiredClientOverride: {
                  points: ep.limit,
                  durationSeconds: unitToDuration(ep.unit)
                }
              }
            });
            currentRevisionToken = overrideResult.clientRevisionToken;
          }

          const editedExceptionGroups = sortExceptionGroups(mapEditedExceptions(ep.exceptions || []));
          const originalExceptionGroups = sortExceptionGroups(mapOriginalExceptions(originalEp));
          const exceptionsChanged = JSON.stringify(editedExceptionGroups) !== JSON.stringify(originalExceptionGroups);

          if (exceptionsChanged) {
            hasChanges = true;
            setSavingGroupId(groupId);
            const exceptionsResult = await saveClientEndpointExceptions({
              endpointId: ep.endpointId,
              data: {
                clientRevisionToken: currentRevisionToken,
                userExceptionGroups: editedExceptionGroups
              }
            });
            currentRevisionToken = exceptionsResult.clientRevisionToken;
          }
        }
      }

      if (hasChanges) {
        await queryClient.invalidateQueries(['clientApiLimits', client?.id]);
        notification.success({ message: 'Saved successfully' });
      }
      setEditingGroupId(null);
    } catch (error: any) {
      notification.error({
        message: 'Failed to update client API limits',
        description: error?.message || 'An unexpected error occurred.'
      });
    } finally {
      setSavingGroupId((prev) => (prev === groupId ? null : prev));
    }
  };

  return (
    <>
      <Row justify="space-between">
        <Col>
          <Typography.Title level={4}>Clients</Typography.Title>
        </Col>
      </Row>

      <Row justify="space-between">
        <Col>
          <SalesforceAccountSearch
            placeholder="Search by supplier Name or Salesforce ID"
            onChange={handleClientChange}
          />
        </Col>
        <Col>
          {aclCheck(['create:suppliers_resellers']) &&
          salesforceAcc?.value &&
          !isLoading &&
          !client &&
          !isAddClicked ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Add
            </Button>
          ) : null}
          {client && !isEditEnabled ? (
            <Button onClick={handleEdit} icon={<EditOutlined />} type="primary">
              Edit Info
            </Button>
          ) : null}
        </Col>
      </Row>

      <Divider className="my-4" />
      <div className="relative">
        <Skeleton loading={isInitialLoading} paragraph={{ rows: 10 }} active>
          {!client && !isInitialLoading && salesforceAcc?.value && <Empty description="No data found!" />}
          {!isLoading && ((client && client?.salesforce_id === salesforceAcc?.value) || (!client && isAddClicked)) ? (
            <ClientForm
              loading={isClientUpdating}
              client={client}
              form={form}
              onFinish={handleSubmit}
              disabled={client && !isEditEnabled}
              onCancel={() => {
                setIsEditEnabled(false);
                setIsAddClicked(false);
              }}
              onSaveLogo={async (res: FileUploadResult) => {
                if (salesforceAcc?.value && res?.key && res?.signedUrl && client) {
                  uploadLogo({ client_id: client.id, logo_filename: res.key });
                }
              }}
              showLogo={!!client}
            />
          ) : null}
          {client && (
            <ClientRelatedCompanies
              client={client}
              deleteRalatedCompany={deleteRalatedCompany}
              handleEditParentCompany={handleEditParentCompany}
            />
          )}
        </Skeleton>
      </div>
      {client && isParentCompanyEditEnabled && (
        <CompanyRelationshipModal
          open={isParentCompanyEditEnabled}
          clientId={client.id}
          salesforceId={client.salesforce_id}
          onCancel={() => setIsParentCompanyEditEnabled(false)}
        />
      )}

      {client && (
        <div className="mt-8">
          <Typography.Title level={4}>API Configurations</Typography.Title>
          {isLoadingApiLimits ? (
            <div className="flex justify-center p-12">
              <Spin size="large" />
            </div>
          ) : (
            <Row gutter={[24, 24]}>
              {apiGroups.map((group) => {
                const isEditing = editingGroupId === group.id;
                const isSaving = savingGroupId === group.id;

                return (
                  <Col xs={24} lg={12} key={group.id}>
                    <ApiCard
                      title={group.title}
                      endpoints={group.endpoints}
                      showExceptions={group.showExceptions}
                      isEditing={isEditing}
                      isSaving={isSaving}
                      userOptions={userOptions}
                      onStartEdit={() => setEditingGroupId(group.id)}
                      onCancelEdit={() => {
                        setEditingGroupId(null);
                        if (clientApiLimits && clientApiLimits.integrations) {
                          const integration = clientApiLimits.integrations.find((i) => i.moduleName === group.id);
                          if (integration) {
                            setApiGroups((prev) =>
                              prev.map((g) =>
                                g.id === group.id
                                  ? {
                                      ...g,
                                      endpoints: integration.endpoints.map((ep) => ({
                                        endpointId: ep.endpointId,
                                        method: ep.httpMethod as HttpMethod,
                                        path: ep.pathTemplate,
                                        limit: ep.effectiveClient?.points ?? ep.effectiveGlobal.points,
                                        unit: durationToUnit(
                                          ep.effectiveClient?.durationSeconds ?? ep.effectiveGlobal.durationSeconds
                                        ),
                                        exceptions: (ep.userExceptionGroups || []).map((ueg) => ({
                                          limit: ueg.limit.points,
                                          unit: durationToUnit(ueg.limit.durationSeconds),
                                          users: ueg.userIds
                                        }))
                                      }))
                                    }
                                  : g
                              )
                            );
                          }
                        }
                      }}
                      onSave={() => handleSaveApiLimits(group.id, group.endpoints)}
                      onUpdateEndpoint={(index: number, patch: any) => {
                        setApiGroups((prev) =>
                          prev.map((g) =>
                            g.id === group.id
                              ? {
                                  ...g,
                                  endpoints: g.endpoints.map((ep, i) => (i === index ? { ...ep, ...patch } : ep))
                                }
                              : g
                          )
                        );
                      }}
                      onAddException={(endpointIndex: number) => {
                        setApiGroups((prev) =>
                          prev.map((g) =>
                            g.id === group.id
                              ? {
                                  ...g,
                                  endpoints: g.endpoints.map((ep, i) =>
                                    i === endpointIndex
                                      ? {
                                          ...ep,
                                          exceptions: [
                                            ...(ep.exceptions || []),
                                            { users: [], limit: null, unit: 'Minute' }
                                          ]
                                        }
                                      : ep
                                  )
                                }
                              : g
                          )
                        );
                      }}
                      onUpdateException={(endpointIndex: number, exceptionIndex: number, patch: any) => {
                        setApiGroups((prev) =>
                          prev.map((g) =>
                            g.id === group.id
                              ? {
                                  ...g,
                                  endpoints: g.endpoints.map((ep, i) =>
                                    i === endpointIndex
                                      ? {
                                          ...ep,
                                          exceptions: (ep.exceptions || []).map((ex, exIdx) =>
                                            exIdx === exceptionIndex ? { ...ex, ...patch } : ex
                                          )
                                        }
                                      : ep
                                  )
                                }
                              : g
                          )
                        );
                      }}
                      onRemoveException={(endpointIndex: number, exceptionIndex: number) => {
                        setApiGroups((prev) =>
                          prev.map((g) =>
                            g.id === group.id
                              ? {
                                  ...g,
                                  endpoints: g.endpoints.map((ep, i) =>
                                    i === endpointIndex
                                      ? {
                                          ...ep,
                                          exceptions: (ep.exceptions || []).filter(
                                            (_, exIdx) => exIdx !== exceptionIndex
                                          )
                                        }
                                      : ep
                                  )
                                }
                              : g
                          )
                        );
                      }}
                      onClearExceptions={(endpointIndex: number) => {
                        setApiGroups((prev) =>
                          prev.map((g) =>
                            g.id === group.id
                              ? {
                                  ...g,
                                  endpoints: g.endpoints.map((ep, i) =>
                                    i === endpointIndex ? { ...ep, exceptions: [] } : ep
                                  )
                                }
                              : g
                          )
                        );
                      }}
                    />
                  </Col>
                );
              })}
            </Row>
          )}
        </div>
      )}
    </>
  );
};

export default Clients;
